import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBarsFilter, faPlus, faTrash, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import {
  GetProduct,
  PutProductArchive,
  PutProductAttach,
  PutProductDetach,
  TGetProduct,
} from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetProductTypes } from "api/rpc/2022-09/facilityAdmin/product/product";
import { GetDepartments as GetFacilityDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { GetVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";

import { Department, IProduct } from "redux/reducers/models/product";
import { filterHistoryUpdateFilter } from "redux/actions/filters/filterHistory";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { sortObj } from "helpers/Helpers";

import { Badge } from "components/badge/Badge";
import { ButtonNew as Button } from "components/buttonNew";
import Page from "components/page/Page";
import Popup from "components/popup/Popup";
import Search from "components/search/Search";

import NumberedPagination from "components/pagination/NumberedPagination";
import FacilityProductTable, { ProductSort, ProductSortKey } from "./FacilityProductTable";
import ProductFiltersDrawer from "./ProductFiltersDrawer";
import ProductsAppliedFilters, { AppliedFilterGroup } from "./ProductsAppliedFilters";
import { ProductFilterValues, buildDepartmentTree, emptyProductFilters } from "./productFilterUtils";

import "./productsHome.scss";

const TABLE_LIMIT = 20;

function scrollParent(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

const FILTER_HISTORY_KEYS = {
  all_facilities: "all_products",
  sales_channel_type: "product_sales_channel_filters",
  type: "product_types_filters",
  department_id: "product_department_filters",
  category_id: "product_category_filters",
  subcategory_id: "product_subcategory_filters",
  vendor_id: "product_vendors_filters",
} as const;

type Option = { id: number; title: string };

export default function Products() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { authStore, facilityStore, filterHistoryStore } = useAppSelector(store => store);
  const permissionToCreate =
    facilityStore.facility?.permissions?.products_create && authStore?.user?.permissions?.products_create;
  const canUseAllFacilitiesFilter =
    !facilityStore?.facility?.is_single_facility && !!authStore?.user?.permissions?.products_view_all;

  function savedFilter<T>(key: keyof typeof FILTER_HISTORY_KEYS): T[] {
    return (filterHistoryStore[FILTER_HISTORY_KEYS[key]]?.currentFilter as T[]) ?? [];
  }

  const [products, setProducts] = useState<IProduct[]>(undefined);
  const [productsCount, setProductsCount] = useState(0);
  const [search, setSearch] = useState<string>(undefined);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<ProductSort>(null);
  const [filters, setFilters] = useState<ProductFilterValues>(() => ({
    type: savedFilter<string>("type"),
    vendor_id: savedFilter<number>("vendor_id"),
    department_id: savedFilter<number>("department_id"),
    category_id: savedFilter<number>("category_id"),
    subcategory_id: savedFilter<number>("subcategory_id"),
    sales_channel_type: savedFilter<"online_store">("sales_channel_type")[0] ?? null,
    all_facilities: canUseAllFacilitiesFilter && !!savedFilter<string>("all_facilities")[0],
  }));
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectionMounted, setSelectionMounted] = useState(false);
  const [selectionLeaving, setSelectionLeaving] = useState(false);
  const [trackedSelectionCount, setTrackedSelectionCount] = useState(0);
  const selectionMountedRef = useRef(false);
  const displayedSelectionCount = useRef(0);
  const productCardRef = useRef<HTMLDivElement>(null);
  const selectionShiftRef = useRef(0);
  const [chromeMotion, setChromeMotion] = useState<"in" | "out" | null>(null);
  const [chromeShift, setChromeShift] = useState(0);
  if (selectedIds.length > 0) {
    displayedSelectionCount.current = selectedIds.length;
  }
  if (selectedIds.length !== trackedSelectionCount) {
    setTrackedSelectionCount(selectedIds.length);
    if (selectedIds.length > 0) {
      selectionMountedRef.current = true;
      setSelectionMounted(true);
      setSelectionLeaving(false);
    } else if (selectionMountedRef.current) {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        selectionMountedRef.current = false;
        setSelectionMounted(false);
        setSelectionLeaving(false);
      } else {
        setSelectionLeaving(true);
      }
    }
  }

  const [departments, setDepartments] = useState<Department[]>(null);
  const [vendors, setVendors] = useState<Option[]>(null);
  const [productTypes, setProductTypes] = useState<Option[]>(null);

  const {
    state: detachProductModal,
    updateModal: updateDetachProductModal,
    closeModal: closeDetachProductModal,
  } = useModal({ productIds: [] as Array<number> });

  const departmentTree = useMemo(
    () =>
      buildDepartmentTree(
        departments?.filter(department => department.type === "department"),
        departments?.filter(department => department.type === "category"),
        departments?.filter(department => department.type === "subcategory"),
      ),
    [departments],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadFilterOptions(source.token);
    return () => source.cancel();
  }, []);

  useLayoutEffect(() => {
    const card = productCardRef.current;
    const scroller = card ? scrollParent(card) : null;
    if (!card || !scroller) {
      return;
    }

    if (!selectionMounted) {
      if (selectionShiftRef.current !== 0) {
        const shift = selectionShiftRef.current;
        scroller.scrollTop -= shift;
        selectionShiftRef.current = 0;
        setChromeShift(shift);
        setChromeMotion("out");
      }
      return;
    }

    if (selectionShiftRef.current !== 0) {
      return;
    }

    const bar = card.previousElementSibling;
    if (!(bar instanceof HTMLElement) || !bar.classList.contains("products-home-selection") || !card.parentElement) {
      return;
    }
    const gap = Number.parseFloat(window.getComputedStyle(card.parentElement).rowGap) || 0;
    const shift = bar.getBoundingClientRect().height + gap;
    selectionShiftRef.current = shift;
    scroller.scrollTop += shift;
    setChromeShift(shift);
    setChromeMotion("in");
  }, [selectionMounted]);

  useEffect(() => {
    if (search === undefined) {
      return;
    }
    const source = axios.CancelToken.source();
    void loadProducts(source.token);
    return () => source.cancel();
  }, [search, offset, sort, filters]);

  async function loadProducts(token?: CancelToken) {
    setProducts(undefined);

    const listFilters: TGetProduct["filters"] = (
      ["type", "vendor_id", "department_id", "category_id", "subcategory_id"] as const
    )
      .filter(key => filters[key].length > 0)
      .map(key => ({ label: key, value: filters[key] }));

    const res = await GetProduct(
      {
        search,
        limit: TABLE_LIMIT,
        offset,
        all: filters.all_facilities || undefined,
        filters: listFilters.length > 0 ? listFilters : undefined,
        department_match: "any",
        sales_channel_type: filters.sales_channel_type ?? undefined,
        sales_channels: true,
        variants: true,
        variant_inventory: true,
        get_count: true,
        sort_by: sort?.key,
        sort_direction: sort?.direction,
      },
      false,
      token,
    );

    if (res.status === StatusCode.OK) {
      const data = res.data as unknown as { product: IProduct[]; count: number };
      setProducts(data.product);
      setProductsCount(data.count);
    }
  }

  async function loadFilterOptions(token: CancelToken) {
    const [departmentRes, vendorRes, typeRes] = await Promise.all([
      GetFacilityDepartments({ extended: false }, false, token),
      GetVendor(null, false, token),
      GetProductTypes(null, false, token),
    ]);

    if (departmentRes.status === StatusCode.OK) {
      setDepartments(sortObj<Department>(departmentRes.data as Department[], "title"));
    }
    if (vendorRes.status === StatusCode.OK) {
      setVendors(sortObj<Option>(vendorRes.data as Option[], "title"));
    }
    if (typeRes.status === StatusCode.OK) {
      const uniqueTypes = (typeRes.data as Option[]).filter(
        (type, index, types) => types.findIndex(other => other.title === type.title) === index,
      );
      setProductTypes(sortObj<Option>(uniqueTypes, "title"));
    }
  }

  function applyFilters(nextFilters: ProductFilterValues) {
    dispatch(
      filterHistoryUpdateFilter(FILTER_HISTORY_KEYS.all_facilities, nextFilters.all_facilities ? ["all-products"] : []),
    );
    dispatch(
      filterHistoryUpdateFilter(
        FILTER_HISTORY_KEYS.sales_channel_type,
        nextFilters.sales_channel_type ? [nextFilters.sales_channel_type] : [],
      ),
    );
    (["type", "vendor_id", "department_id", "category_id", "subcategory_id"] as const).forEach(key =>
      dispatch(filterHistoryUpdateFilter(FILTER_HISTORY_KEYS[key], nextFilters[key])),
    );

    setFilters(nextFilters);
    setOffset(0);
  }

  function removeFilterGroup(group: AppliedFilterGroup) {
    const cleared: Record<AppliedFilterGroup, Partial<ProductFilterValues>> = {
      type: { type: [] },
      vendor: { vendor_id: [] },
      availability: { sales_channel_type: null, all_facilities: false },
      departments: { department_id: [], category_id: [], subcategory_id: [] },
    };
    applyFilters({ ...filters, ...cleared[group] });
  }

  function handleSortChange(key: ProductSortKey) {
    setSort(prev => {
      if (prev?.key !== key) {
        return { key, direction: "asc" };
      }
      return prev.direction === "asc" ? { key, direction: "desc" } : null;
    });
    setOffset(0);
  }

  async function attachProductToFacility(event: React.MouseEvent<HTMLElement>, productId: number) {
    event.stopPropagation();
    const res = await PutProductAttach({ product_id: productId }, true);
    if (res.status === StatusCode.OK) {
      setProducts(prev =>
        prev.map(product => (product.id === productId ? { ...product, facility_access: true } : product)),
      );
    }
  }

  async function detachProductFromFacility() {
    const res = facilityStore?.facility?.is_single_facility
      ? await PutProductArchive({ product_ids: detachProductModal.productIds }, true)
      : await PutProductDetach({ product_ids: detachProductModal.productIds }, true);
    if (res.status !== StatusCode.OK) {
      return;
    }
    closeDetachProductModal();
    void loadProducts();
  }

  return (
    <Page
      className="products-home-page"
      title={
        <h1
          className={`products-home-heading${chromeMotion ? ` is-chrome-${chromeMotion}` : ""}`}
          style={{ "--selection-shift": `${chromeShift}px` } as React.CSSProperties}
        >
          {t("secure.facility.product.products.002")}
          <Badge type="grayblue" size="small">
            {productsCount ?? 0}
          </Badge>
        </h1>
      }
      subtitle={t("View and manage all the products for your facility here.")}
      primaryAction={{
        content: (
          <span className="products-home-new">
            <FontAwesomeIcon icon={faPlus} />
            {t("New product")}
          </span>
        ),
        action: () => history.push("/admin/product/new"),
        disabled: !permissionToCreate,
      }}
    >
      <div className="products-home">
        <div
          className={`products-home-toolbar${chromeMotion ? ` is-chrome-${chromeMotion}` : ""}`}
          style={{ "--selection-shift": `${chromeShift}px` } as React.CSSProperties}
        >
          <div className="products-home-search">
            <Search
              searchCallback={searchValue => {
                setSearch(searchValue);
                setOffset(0);
              }}
              historyKey="products-all"
              restrictPresetToRoutes={["admin/product"]}
              placeholder={t("Search a product...")}
            />
          </div>
          <Button type="secondary" size="medium" onClick={() => setFilterDrawerOpen(true)}>
            <FontAwesomeIcon icon={faBarsFilter} />
            {t("Filter")}
          </Button>
        </div>

        <ProductsAppliedFilters
          filters={filters}
          vendors={vendors}
          departmentTree={departmentTree}
          onRemoveGroup={removeFilterGroup}
          onClearAll={() => applyFilters(emptyProductFilters())}
          onEditFilters={() => setFilterDrawerOpen(true)}
        />

        {selectionMounted ? (
          <div
            className={selectionLeaving ? "products-home-selection is-leaving" : "products-home-selection"}
            onAnimationEnd={event => {
              if (event.target !== event.currentTarget || event.animationName !== "products-home-selection-out") {
                return;
              }
              selectionMountedRef.current = false;
              setSelectionMounted(false);
              setSelectionLeaving(false);
            }}
          >
            <span className="products-home-selection-count">
              {t("{{count}} selected", { count: displayedSelectionCount.current })}
            </span>
            <span className="products-home-selection-divider" aria-hidden="true" />
            <button
              type="button"
              className="products-home-selection-remove"
              onClick={() => {
                updateDetachProductModal({ isOpen: true, productIds: selectedIds });
                setSelectedIds([]);
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
              {t("Remove products")}
            </button>
            <span className="products-home-selection-divider" aria-hidden="true" />
            <button type="button" className="products-home-selection-clear" onClick={() => setSelectedIds([])}>
              <FontAwesomeIcon icon={faXmark} />
              {t("Clear all")}
            </button>
          </div>
        ) : null}

        <div className="products-home-card" ref={productCardRef}>
          <FacilityProductTable
            products={products}
            sort={sort}
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
            onSortChange={handleSortChange}
            attachProductToFacility={(event, productId) => void attachProductToFacility(event, productId)}
          />

          <NumberedPagination
            currentPage={Math.floor(offset / TABLE_LIMIT) + 1}
            totalPages={Math.max(1, Math.ceil((productsCount ?? 0) / TABLE_LIMIT))}
            disabled={products === undefined}
            onPageChange={page => setOffset((page - 1) * TABLE_LIMIT)}
          />
        </div>
      </div>

      <ProductFiltersDrawer
        open={filterDrawerOpen}
        appliedFilters={filters}
        productTypes={productTypes}
        vendors={vendors}
        departmentTree={departmentTree}
        canUseAllFacilitiesFilter={canUseAllFacilitiesFilter}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={applyFilters}
      />

      <Popup
        open={detachProductModal.isOpen}
        onCancel={closeDetachProductModal}
        onOk={detachProductFromFacility}
        type="warning"
        title={t("secure.facility.product.products.012")}
        description={t("secure.facility.product.products.013")}
      />
    </Page>
  );
}
